<template>
  <div :class="[prefixCls, getAlign]" @click="onCellClick">
    <template v-for="(action, index) in getActions" :key="`${index}-${action.label}`">
      <Tooltip v-if="action.tooltip" v-bind="getTooltip(action.tooltip)">
        <PopConfirmButton v-bind="omit(action, ['icon', 'label'])">
          <MIcon
            v-if="action.icon"
            :icon="action.icon"
            :size="action.iconSize"
            :class="{ 'mr-1': !!action.label }"
          />
          <template v-if="action.label">{{ action.label }}</template>
        </PopConfirmButton>
      </Tooltip>
      <PopConfirmButton v-else v-bind="omit(action, ['icon', 'label'])">
        <MIcon
          v-if="action.icon"
          :icon="action.icon"
          :size="action.iconSize"
          :class="{ 'mr-1': !!action.label }"
        />
        <template v-if="action.label">{{ action.label }}</template>
      </PopConfirmButton>
      <Divider
        v-if="divider && index < getActions.length - 1"
        type="vertical"
        class="action-divider"
      />
    </template>
    <Dropdown
      v-if="dropDownActions && getDropdownList.length > 0"
      :trigger="['hover']"
      :options="getDropdownList"
      popconfirm
    >
      <slot name="more"></slot>
      <MButton v-if="!$slots.more" type="link" size="small">
        <MoreOutlined class="icon-more" />
      </MButton>
    </Dropdown>
  </div>
</template>
<script lang="ts" setup>
  import { MoreOutlined } from '@ant-design/icons-vue';
  import { Divider, Tooltip, TooltipProps } from 'ant-design-vue';
  import { computed, PropType, toRaw, unref } from 'vue';
  import { useRoute } from 'vue-router';
  import { isIfShow, isString, omit, propTypes } from '@mts-fe/utils';
  import { PopConfirmButton } from '@mts/components/Button';
  import { Dropdown } from '@mts/components/Dropdown';
  import { MIcon } from '@mts/components/Icon';
  import { ActionItem, TableActionType } from '@mts/components/Table';
  import { useDesign } from '@mts/hooks/web/useDesign';
  import { t } from '@mts/hooks/web/useI18n';
  import { usePermission } from '@mts/hooks/web/usePermission';
  import { getGlobalStore } from '@/store';
  import { ACTION_COLUMN_FLAG } from '../const';
  import { useTableContext } from '../hooks/useTableContext';

  defineOptions({ name: 'TableAction' });

  const props = defineProps({
    actions: {
      type: Array as PropType<ActionItem[]>,
      default: null,
    },
    dropDownActions: {
      type: Array as PropType<ActionItem[]>,
      default: null,
    },
    divider: propTypes.bool.def(true),
    outside: propTypes.bool,
    stopButtonPropagation: propTypes.bool.def(true),
  });

  const { prefixCls } = useDesign('basic-table-action');
  let table: Partial<TableActionType> = {};
  if (!props.outside) {
    table = useTableContext();
  }

  const route = useRoute();
  const globalStore = getGlobalStore();
  const { hasPermission } = usePermission();
  const getActions = computed(() => {
    const globalParams = { route, sysConfig: globalStore.getConfig };
    return (toRaw(props.actions) || [])
      .filter((action) => {
        return hasPermission(action.auth) && isIfShow<ActionItem>(action, globalParams);
      })
      .map((action) => {
        const { popConfirm } = action;
        return {
          getPopupContainer: () => unref((table as any)?.wrapRef) ?? document.body,
          type: 'link',
          size: 'small',
          ...omit(action, ['auth', 'popConfirm']),
          label: typeof action.label === 'string' ? t(action.label) : action.label,
          ...(popConfirm || {}),
          onConfirm: popConfirm?.confirm,
          onCancel: popConfirm?.cancel,
          enable: !!popConfirm,
        };
      });
  });

  const getDropdownList = computed((): any[] => {
    const globalParams = { route, sysConfig: globalStore.getConfig };
    const list = (toRaw(props.dropDownActions) || []).filter((action) => {
      return hasPermission(action.auth) && isIfShow<ActionItem>(action, globalParams);
    });
    return list.map((action, index) => {
      const { popConfirm } = action;
      return {
        ...action,
        ...popConfirm,
        onConfirm: popConfirm?.confirm,
        onCancel: popConfirm?.cancel,
        divider: index < list.length - 1 ? props.divider : false,
      };
    });
  });

  const getAlign = computed(() => {
    const columns = (table as TableActionType)?.getColumns?.() || [];
    const actionColumn = columns.find((item) => item.flag === ACTION_COLUMN_FLAG);
    return actionColumn?.align ?? 'left';
  });

  function getTooltip(data: string | TooltipProps): TooltipProps {
    return {
      getPopupContainer: () => unref((table as any)?.wrapRef) ?? document.body,
      placement: 'bottom',
      ...(isString(data) ? { title: data } : data),
    };
  }

  function onCellClick(e: MouseEvent) {
    if (!props.stopButtonPropagation) return;
    const path = e.composedPath() as HTMLElement[];
    const isInButton = path.find((ele) => {
      return ele.tagName?.toUpperCase() === 'BUTTON';
    });
    isInButton && e.stopPropagation();
  }
</script>
<style lang="less">
  @prefix-cls: ~'@{namespace}-basic-table-action';

  .@{prefix-cls} {
    display: flex;
    flex-wrap: wrap;
    align-items: center;

    .action-divider {
      display: table;
    }

    &.left {
      justify-content: flex-start;
    }

    &.center {
      justify-content: center;
    }

    &.right {
      justify-content: flex-end;
    }

    button {
      display: flex;
      align-items: center;

      span {
        margin-left: 0 !important;
      }
    }

    button.ant-btn-circle {
      span {
        margin: auto !important;
      }
    }

    .ant-divider,
    .ant-divider-vertical {
      margin: 0 2px;
    }

    .icon-more {
      transform: rotate(90deg);

      svg {
        font-size: 1.1em;
        font-weight: 700;
      }
    }
  }
</style>
